/**
 * JSON Tree Viewer — general-purpose, reusable utility.
 * Renders a collapsible, color-coded JSON tree (VS Code dark palette).
 * Used by: JsonEditor (tree toggle), RawConfigModal.
 */

import React, { useState } from 'react';

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
interface JsonObject { [key: string]: JsonValue; }
type JsonArray = JsonValue[];

const C = {
  key:     '#9cdcfe',
  string:  '#ce9178',
  number:  '#b5cea8',
  boolean: '#569cd6',
  null:    '#b4cea8',
  punct:   'rgba(255,255,255,0.4)',
};

const ToggleBtn: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      background: 'none', border: 'none', cursor: 'pointer',
      color: C.punct, padding: '0 2px', fontSize: '9px',
      lineHeight: 1, verticalAlign: 'middle',
    }}
  >
    {open ? '▼' : '▶'}
  </button>
);

const JsonNode: React.FC<{ value: JsonValue; depth?: number }> = ({ value, depth = 0 }) => {
  const [open, setOpen] = useState(depth < 3);
  const pad = depth * 16;

  if (value === null)              return <span style={{ color: C.null }}>null</span>;
  if (typeof value === 'boolean')  return <span style={{ color: C.boolean }}>{String(value)}</span>;
  if (typeof value === 'number')   return <span style={{ color: C.number }}>{value}</span>;
  if (typeof value === 'string')   return <span style={{ color: C.string }}>"{value}"</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: C.punct }}>[]</span>;
    return (
      <>
        <ToggleBtn open={open} onClick={() => setOpen(o => !o)} />
        <span style={{ color: C.punct }}>[</span>
        {open ? (
          <>
            {value.map((item, i) => (
              <div key={i} style={{ paddingLeft: `${pad + 16}px` }}>
                <JsonNode value={item} depth={depth + 1} />
                {i < value.length - 1 && <span style={{ color: C.punct }}>,</span>}
              </div>
            ))}
            <div><span style={{ color: C.punct }}>]</span></div>
          </>
        ) : (
          <>
            <span
              style={{ color: 'rgba(255,255,255,0.3)', cursor: 'pointer', margin: '0 4px' }}
              onClick={() => setOpen(true)}
            >
              {value.length} items…
            </span>
            <span style={{ color: C.punct }}>]</span>
          </>
        )}
      </>
    );
  }

  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as JsonObject);
    if (entries.length === 0) return <span style={{ color: C.punct }}>{'{}'}</span>;
    return (
      <>
        <ToggleBtn open={open} onClick={() => setOpen(o => !o)} />
        <span style={{ color: C.punct }}>{'{'}</span>
        {open ? (
          <>
            {entries.map(([k, v], i) => (
              <div key={k} style={{ paddingLeft: `${pad + 16}px` }}>
                <span style={{ color: C.key }}>"{k}"</span>
                <span style={{ color: C.punct }}>: </span>
                <JsonNode value={v} depth={depth + 1} />
                {i < entries.length - 1 && <span style={{ color: C.punct }}>,</span>}
              </div>
            ))}
            <div><span style={{ color: C.punct }}>{'}'}</span></div>
          </>
        ) : (
          <>
            <span
              style={{ color: 'rgba(255,255,255,0.3)', cursor: 'pointer', margin: '0 4px' }}
              onClick={() => setOpen(true)}
            >
              {entries.length} keys…
            </span>
            <span style={{ color: C.punct }}>{'}'}</span>
          </>
        )}
      </>
    );
  }

  return null;
};

export interface JsonViewerProps {
  value: string;
  /** CSS height / max-height for the scrollable tree container */
  maxHeight?: string;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({ value, maxHeight = '100%' }) => {
  let parsed: JsonValue | undefined;
  let parseError: string | null = null;

  try {
    if (value.trim()) parsed = JSON.parse(value);
  } catch (e: any) {
    parseError = e.message;
  }

  const baseStyle: React.CSSProperties = {
    background: 'rgba(20, 30, 48, 0.8)',
    border: '1px solid #334155',
    borderRadius: '6px',
    padding: '12px 16px',
    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace",
    fontSize: '12px',
    lineHeight: '1.8',
    maxHeight,
    overflow: 'auto',
    color: '#e2e8f0',
  };

  if (!value.trim()) {
    return <div style={{ ...baseStyle, color: 'rgba(255,255,255,0.3)' }}>No data to display</div>;
  }

  if (parseError) {
    return <div style={{ ...baseStyle, color: '#ef4444' }}>Invalid JSON: {parseError}</div>;
  }

  return (
    <div style={baseStyle}>
      <JsonNode value={parsed!} />
    </div>
  );
};
